import {Anchor, Badge, Group, Paper, Stack, Table, Text, Title} from '@mantine/core'
import {IconAlertTriangle} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'

import {projectBillingTermsLabel} from '../jobs/billing-terms-display'
import {toLifecycleStatus} from '../jobs/job-api-adapter'
import {JobStatusBadge} from '../jobs/job-status-badge'
import {fulfillmentStatusColor, identityScheduleColor} from '../theme/semantic-colors'
import {PartialPickupIcon} from '../work/partial-pickup'
import type {PartialPickup} from '../work/partial-pickup-schema'
import {
    formatFulfillmentLines,
    formatJobDateLines,
    formatJobWindow,
    type FulfillmentLine,
} from './project-jobs-format'
import type {ProjectBillingTerms, ProjectJobSummary, ProjectLifecycleStatus} from './schemas'

type ProjectJobsSectionProps = {
    projectId: string
    jobs: ProjectJobSummary[]
    projectStatus: ProjectLifecycleStatus
    billingTerms?: ProjectBillingTerms[]
}

type JobRowViewModel = {
    id: string
    name: string
    displayId: string
    dateLines: {primary: string; secondary: string | null}
    windowText: string
    isScheduled: boolean
    scheduleBadgeLabel: string
    fulfillmentLines: FulfillmentLine[]
    status: ProjectJobSummary['status']
    poNumber: string | null
    billingTermsLabel: string
    dimmed: boolean
    partialPickup: PartialPickup | null
}

function toRowViewModel(
    job: ProjectJobSummary,
    billingTermsById: Map<string, ProjectBillingTerms>,
): JobRowViewModel {
    return {
        id: job.id,
        name: job.name,
        displayId: job.displayId,
        partialPickup: job.partialPickup ?? null,
        dateLines: job.dateTbc
            ? {primary: 'date TBC', secondary: null}
            : formatJobDateLines(job.startDate, job.endDate),
        windowText: formatJobWindow(job.startTime, null),
        isScheduled: job.serviceScheduleId != null,
        scheduleBadgeLabel: job.serviceScheduleServiceTypeName ?? 'One-off',
        fulfillmentLines: formatFulfillmentLines(job.requirementFulfillment),
        status: job.status,
        poNumber: job.poNumber,
        billingTermsLabel: projectBillingTermsLabel(
            job.projectBillingTermsId == null
                ? null
                : billingTermsById.get(job.projectBillingTermsId),
        ),
        dimmed: job.status === 'cancelled',
    }
}

function ScheduleCell({row}: {row: JobRowViewModel}) {
    if (!row.isScheduled) {
        return (
            <Badge color="gray" variant="light">
                One-off
            </Badge>
        )
    }
    return (
        <Badge color={identityScheduleColor()} variant="light">
            {row.scheduleBadgeLabel}
        </Badge>
    )
}

function RequirementsCell({row}: {row: JobRowViewModel}) {
    if (row.fulfillmentLines.length === 0) {
        return (
            <Text size="sm" c={row.dimmed ? 'dimmed' : undefined}>
                None
            </Text>
        )
    }

    return (
        <Stack gap={2}>
            {row.fulfillmentLines.map((line) => {
                const isUnderFilled = !row.dimmed && line.status === 'under'
                const color = row.dimmed ? undefined : fulfillmentStatusColor(line.status)
                const stateLabel = isUnderFilled ? ', under-filled' : ''
                return (
                    <Group key={line.kind} gap={4} wrap="nowrap">
                        <Text
                            size="sm"
                            c={row.dimmed ? 'dimmed' : color}
                            aria-label={`${line.label}${stateLabel}`}
                        >
                            {line.label}
                        </Text>
                        {isUnderFilled ? (
                            <IconAlertTriangle
                                size={12}
                                color="var(--mt-signal-pending)"
                                aria-hidden="true"
                            />
                        ) : null}
                    </Group>
                )
            })}
        </Stack>
    )
}

function JobsSectionHeader({jobs}: {jobs: ProjectJobSummary[]}) {
    return (
        <Group gap="sm" align="center">
            <Title order={4}>Jobs</Title>
            <Badge variant="light" color="gray">
                {jobs.length}
            </Badge>
        </Group>
    )
}

const scheduleRailStyle = {boxShadow: 'inset 3px 0 0 var(--mt-identity-schedule-subtle)'}

function JobsTable({
    rows,
    onRowClick,
}: {
    rows: JobRowViewModel[]
    onRowClick: (id: string) => void
}) {
    return (
        <Table highlightOnHover visibleFrom="sm">
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Job</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Window</Table.Th>
                    <Table.Th>Schedule</Table.Th>
                    <Table.Th>PO / terms</Table.Th>
                    <Table.Th>Requirements</Table.Th>
                    <Table.Th>Status</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows.map((row) => (
                    <Table.Tr
                        key={row.id}
                        onClick={() => onRowClick(row.id)}
                        style={{cursor: 'pointer'}}
                    >
                        <Table.Td style={row.isScheduled ? scheduleRailStyle : undefined}>
                            <Stack gap={0}>
                                <Anchor
                                    component={Link}
                                    to="/jobs/$jobId"
                                    params={{jobId: row.id}}
                                    size="sm"
                                    fw={500}
                                    c={row.dimmed ? 'dimmed' : undefined}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {row.name}
                                </Anchor>
                                <Group gap={4} wrap="nowrap">
                                    <Text size="xs" c="dimmed" ff="monospace">
                                        {row.displayId}
                                    </Text>
                                    <PartialPickupIcon partialPickup={row.partialPickup} />
                                </Group>
                            </Stack>
                        </Table.Td>
                        <Table.Td>
                            <Stack gap={0}>
                                <Text size="sm" c={row.dimmed ? 'dimmed' : undefined}>
                                    {row.dateLines.primary}
                                </Text>
                                {row.dateLines.secondary ? (
                                    <Text size="xs" c="dimmed">
                                        {row.dateLines.secondary}
                                    </Text>
                                ) : null}
                            </Stack>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm" c={row.dimmed ? 'dimmed' : undefined}>
                                {row.windowText}
                            </Text>
                        </Table.Td>
                        <Table.Td>
                            <ScheduleCell row={row} />
                        </Table.Td>
                        <Table.Td>
                            <Stack gap={0}>
                                <Text size="sm">{row.poNumber ?? 'No PO'}</Text>
                                <Text size="xs" c="dimmed">
                                    {row.billingTermsLabel}
                                </Text>
                            </Stack>
                        </Table.Td>
                        <Table.Td>
                            <RequirementsCell row={row} />
                        </Table.Td>
                        <Table.Td>
                            <JobStatusBadge status={toLifecycleStatus(row.status)} />
                        </Table.Td>
                    </Table.Tr>
                ))}
            </Table.Tbody>
        </Table>
    )
}

function JobsStackedList({
    rows,
    onRowClick,
}: {
    rows: JobRowViewModel[]
    onRowClick: (id: string) => void
}) {
    return (
        <Stack gap="sm" hiddenFrom="sm">
            {rows.map((row) => (
                <Paper
                    key={row.id}
                    withBorder
                    p="sm"
                    radius="md"
                    onClick={() => onRowClick(row.id)}
                    style={{
                        cursor: 'pointer',
                        ...(row.isScheduled ? scheduleRailStyle : {}),
                    }}
                >
                    <Stack gap={4}>
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Stack gap={0}>
                                <Anchor
                                    component={Link}
                                    to="/jobs/$jobId"
                                    params={{jobId: row.id}}
                                    size="sm"
                                    fw={500}
                                    c={row.dimmed ? 'dimmed' : undefined}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {row.name}
                                </Anchor>
                                <Group gap={4} wrap="nowrap">
                                    <Text size="xs" c="dimmed" ff="monospace">
                                        {row.displayId}
                                    </Text>
                                    <PartialPickupIcon partialPickup={row.partialPickup} />
                                </Group>
                            </Stack>
                            <JobStatusBadge status={toLifecycleStatus(row.status)} />
                        </Group>
                        <Group gap="xs">
                            <Text size="sm" c={row.dimmed ? 'dimmed' : undefined}>
                                {row.dateLines.primary}
                                {row.dateLines.secondary ? ` – ${row.dateLines.secondary}` : ''}
                            </Text>
                            <Text size="sm" c={row.dimmed ? 'dimmed' : undefined}>
                                {row.windowText}
                            </Text>
                        </Group>
                        <Group gap="xs" align="center">
                            <ScheduleCell row={row} />
                            <Text size="xs" c="dimmed">
                                PO: {row.poNumber ?? 'No PO'} · terms {row.billingTermsLabel}
                            </Text>
                        </Group>
                        <RequirementsCell row={row} />
                    </Stack>
                </Paper>
            ))}
        </Stack>
    )
}

export function ProjectJobsSection({jobs, billingTerms = []}: ProjectJobsSectionProps) {
    const navigate = useNavigate()
    const billingTermsById = new Map(billingTerms.map((terms) => [terms.id, terms]))
    const rows = jobs.map((job) => toRowViewModel(job, billingTermsById))

    function handleRowClick(jobId: string) {
        void navigate({to: '/jobs/$jobId', params: {jobId}})
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <JobsSectionHeader jobs={jobs} />
                {rows.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No operational jobs on this order yet.
                    </Text>
                ) : (
                    <>
                        <JobsTable rows={rows} onRowClick={handleRowClick} />
                        <JobsStackedList rows={rows} onRowClick={handleRowClick} />
                    </>
                )}
            </Stack>
        </Paper>
    )
}
